import { apiSend } from "@/lib/api/client";
import { slackChannelsContract } from "@/lib/api/contracts/plugins";

export type SlackChannelOption = {
  id: string;
  name: string;
  createdAt?: number;
  /** Whether the bot is a member. Public channels are postable via
   *  chat:write.public even when false; private channels are not. */
  isMember?: boolean;
  /** Private (or group) channel — invite-only for posting. */
  isPrivate?: boolean;
};

/** Placeholder workspace channels until `/api/plugins/slack/channels` is wired up. */
export const PLACEHOLDER_SLACK_CHANNELS: SlackChannelOption[] = [
  { id: "C200KENT", name: "200-kent" },
  { id: "C1ELITE", name: "elite-level-ball-knowledge" },
  { id: "C1EXT", name: "ext-profound-layo" },
  { id: "C1GENERAL", name: "general" },
  { id: "C1HIRING", name: "hiring-canidates" },
  { id: "C1INC", name: "incidents" },
  { id: "C1INSP", name: "inspiration" },
  { id: "C1DEMO", name: "layo-demo" },
];

export function slackChannelLabel(
  channelId: string,
  channels: ReadonlyArray<SlackChannelOption> = PLACEHOLDER_SLACK_CHANNELS,
): string {
  return channels.find((channel) => channel.id === channelId)?.name ?? channelId;
}

export function formatSlackChannelSelectionLabel(opts: {
  scope: "unset" | "any" | "selected";
  channelIds: string[];
  channels?: ReadonlyArray<SlackChannelOption>;
}): string {
  if (opts.scope === "any") return "Any channel";
  if (opts.scope !== "selected" || opts.channelIds.length === 0) {
    return "Select channel";
  }

  const channels = opts.channels ?? PLACEHOLDER_SLACK_CHANNELS;
  if (opts.channelIds.length === 1) {
    return slackChannelLabel(opts.channelIds[0], channels);
  }
  return `${opts.channelIds.length} channels`;
}

export function serializeSlackChannelSelection(opts: {
  scope: "unset" | "any" | "selected";
  channelIds: string[];
  channels?: ReadonlyArray<SlackChannelOption>;
}): string {
  if (opts.scope === "any") return "any channel";
  if (opts.scope !== "selected" || opts.channelIds.length === 0) {
    return "select channel";
  }

  const channels = opts.channels ?? PLACEHOLDER_SLACK_CHANNELS;
  return opts.channelIds
    .map((channelId) => slackChannelLabel(channelId, channels))
    .join(", ");
}

export function parseSlackChannelSelection(
  channelText: string,
  channels: ReadonlyArray<SlackChannelOption> = PLACEHOLDER_SLACK_CHANNELS,
): {
  channelScope: "unset" | "any" | "selected";
  channelIds: string[];
} {
  const normalized = channelText.trim().toLowerCase();
  if (normalized === "any channel") {
    return { channelScope: "any", channelIds: [] };
  }
  if (normalized === "select channel") {
    return { channelScope: "unset", channelIds: [] };
  }

  const names = channelText
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  const channelIds = names.map((name) => {
    const byName = channels.find(
      (channel) => channel.name.toLowerCase() === name.toLowerCase(),
    );
    if (byName) return byName.id;
    if (/^C[A-Z0-9]+$/i.test(name)) return name.toUpperCase();
    return name;
  });

  return {
    channelScope: channelIds.length > 0 ? "selected" : "unset",
    channelIds,
  };
}

export type SlackChannelOptionsResult =
  | { ok: true; channels: SlackChannelOption[] }
  | { ok: false; error: string };

/**
 * Loads the connected workspace's Slack channels for the current org. Surfaces
 * failures explicitly — we never substitute fabricated channels, since a user
 * could otherwise select and save a channel that doesn't exist.
 */
export async function fetchSlackChannelOptions(): Promise<SlackChannelOptionsResult> {
  const result = await apiSend(slackChannelsContract);
  if (!result.ok) {
    return { ok: false, error: result.error.message };
  }
  return {
    ok: true,
    channels: Array.isArray(result.data.channels) ? result.data.channels : [],
  };
}
